import { EXTERNAL_SCOPE_TYPES } from './messaging-user';
import { MODEL_NAMES } from '../constants/index';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { isSameConversation, sortConversationMembers } from '../utils/messaging/conversation';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';

export const MAX_PARTICIPANTS = 9;

export default class MessagingConversationModel extends Model {
  @hasMany(MODEL_NAMES.CONTACT, { async: false, inverse: null }) contacts;
  @hasMany(MODEL_NAMES.USER, { async: false, inverse: null }) users;
  @hasMany(MODEL_NAMES.MESSAGE, { async: false, inverse: 'conversation' }) messages;
  @belongsTo(MODEL_NAMES.MESSAGE, { async: false, inverse: null }) lastMessage;

  @attr('boolean', { defaultValue: false }) disabled;
  @attr('boolean', { defaultValue: false }) inactive;
  @attr('boolean', { defaultValue: false }) isCouple;
  @attr('boolean', { defaultValue: false }) isDraft;
  @attr('boolean', { defaultValue: false }) isGroup;

  @attr('string') displayName;
  @attr('string') linkExternalId; // Id for couple record if one is present in the conversation.
  @attr('string', { defaultValue: '' }) messageDraft;

  @attr('date') readAt;
  @attr('date') updatedAt;

  @attr({
    defaultValue() {
      return [];
    },
  })
  conflicts;

  @attr({
    defaultValue() {
      return {};
    },
  })
  counterpartReadAt;

  // { userId: messageId } of the latest own message each counterpart has read.
  // Populated on MESSAGING_API_LATEST_VERSION (2026-05-26) when groupMessagingEnabled is on.
  // Absent/empty on MESSAGING_API_LEGACY_VERSION (2021-06-10).
  @attr({
    defaultValue() {
      return {};
    },
  })
  counterpartReadMessageIds;

  // User ids whose membership is disabled (left / removed)
  @attr({
    defaultValue() {
      return [];
    },
  })
  disabledMembers;

  get messagesByCreatedAt() {
    return this.messages?.slice()?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  get mostRecentOwnMessage() {
    return this.messagesByCreatedAt?.find(message => !message.isSystem && message.isOwn);
  }

  get mostRecentReadAt() {
    const sortedReadAts = Object.values(this.counterpartReadAt).sort(
      (a, b) => new Date(b) - new Date(a)
    );
    return sortedReadAts.length ? new Date(sortedReadAts[0]) : new Date(this.readAt);
  }

  get lastReadOwnMessage() {
    return this.messagesByCreatedAt?.find(
      message => message.isOwn && new Date(message.createdAt) <= this.mostRecentReadAt
    );
  }

  readersForMessage(messageId) {
    if (messageId === null || messageId === undefined) return [];

    const users = this.users ?? [];

    return Object.entries(this.counterpartReadMessageIds ?? {})
      .filter(([, readMessageId]) => String(readMessageId) === String(messageId))
      .map(([userId]) => users.find(user => String(user.id) === String(userId)))
      .filter(Boolean);
  }

  get hasMessageDraft() {
    return this.messageDraft.trim().length > 0;
  }

  isSameId(otherId) {
    return String(this.id) === String(otherId);
  }

  /**
   * Promote this conversation from a message that has been persisted.
   * Shared by the sender's successful POST and `message.created`. Either path
   * may run first; repeating it does not create another message. System
   * messages stay server-owned and do not promote a draft.
   */
  applyCreatedMessage(message) {
    if (!message || message.isSystem) {
      return;
    }

    this.isDraft = false;
    this.updatedAt = new Date();
    this.lastMessage = message;
  }

  /**
   * Same live Conversation as `other`: identical record, or both persisted
   * with the same id. Two unsaved records are not the same unless they are
   * the same object — missing ids must not collapse routes onto each other.
   */
  isSameConversation(other) {
    return isSameConversation(this, other);
  }

  // Canonical roster of the conversation across pre- and post-save states:
  // staged `contacts` for unsaved conversations, server-owned `users` once
  // persisted. The `isNew` gate keeps post-save reads from ever reading a
  // stale `contacts` snapshot.
  get intendedMembers() {
    if (this.isNew && this.contacts?.length) {
      return this.contacts.flatMap(contact => contact.members ?? [contact]);
    }
    return this.users ?? [];
  }

  get hasPreviousMembers() {
    return this.previousMembers.length > 0;
  }

  // Active roster with a couple shown as one entry. A couple stays here while any
  // partner is still active, including when the other partner is disabled.
  get collapsedMembers() {
    return this.#collapsedRoster.filter(member => !this.#isPrevious(member));
  }

  // Disabled people. Both partners of a couple collapse into one entry, and only
  // once both are disabled. One disabled partner stays with the couple in collapsedMembers.
  get previousMembers() {
    return this.#collapsedRoster.filter(member => this.#isPrevious(member));
  }

  isMembershipDisabled(member) {
    const disabledMembers = this.disabledMembers ?? [];
    return disabledMembers.some(id => String(id) === String(member.id));
  }

  isSoleRemainingMember(currentUser) {
    if (!currentUser) return false;

    const members = this.intendedMembers.filter(member => !this.isMembershipDisabled(member));
    if (members.length === 0) return false;

    return filterSystemAndCurrentUser(members, currentUser).length === 0;
  }

  isInactiveSoleRemainingGroup(currentUser) {
    return Boolean(this.inactive && this.isGroup && this.isSoleRemainingMember(currentUser));
  }

  canModifyMembers(profile) {
    return Boolean(profile?.isClinician && this.isDraft && this.isGroup);
  }

  canNameConversation(profile) {
    return this.canModifyMembers(profile);
  }

  canAddTeamMembers(profile) {
    return Boolean(profile?.isClinician && this.isGroup && !this.isDraft);
  }

  // Everyone in the conversation, with a couple drawn as one entry.
  get #collapsedRoster() {
    const couple = this.#collapsedCouple();
    const people = this.intendedMembers.filter(member => !couple || !member.isClient);
    const roster = couple ? [...people, couple] : people;

    return sortConversationMembers(roster);
  }

  #isPrevious(member) {
    if (member.isCouple) return this.#bothPartnersDisabled(member);
    return this.isMembershipDisabled(member);
  }

  // The couple as one entry, or null when this conversation has fewer than two clients.
  #collapsedCouple() {
    const clients = this.#coupleClients();
    if (clients.length < 2) return null;

    const externalId = this.#coupleExternalId(clients);

    return {
      isCouple: true,
      id: externalId,
      externalId,
      externalScope: EXTERNAL_SCOPE_TYPES.client,
      name: clients
        .map(client => client.name)
        .filter(Boolean)
        .join(' & '),
      isClient: true,
      members: clients,
    };
  }

  #bothPartnersDisabled(couple) {
    return couple.members.every(member => this.isMembershipDisabled(member));
  }

  #coupleClients() {
    if (!this.isCouple) return [];
    return this.intendedMembers
      .filter(member => member.isClient)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  #coupleExternalId(clients) {
    if (this.linkExternalId) return String(this.linkExternalId);

    if (this.isNew) {
      const stagedCouple = this.contacts?.find(contact => contact.couple?.length);
      if (stagedCouple?.externalId) return String(stagedCouple.externalId);
    }

    return String(clients[0].externalId);
  }
}
